import { defineStore } from 'pinia';
import INotification from '@/interfaces/INotification';
import { ToastVariant } from '@/enums/ToastVariant';
import { generateUniqueId } from '@/helpers/uniqueId';

export const useNotificationStore = defineStore('notifications', {
  state: () => ({
    notifications: [] as INotification[],
  }),
  getters: {
    getActiveNotifications(): INotification[] {
      return this.notifications.filter((n) => n.isVisible);
    },
    getAllNotifications(): INotification[] {
      return this.notifications;
    },
    getReversedActiveNotifications(): INotification[] {
      return this.getActiveNotifications.slice().reverse();
    }
  },
  actions: {
    addNotification(
      toastVariant: ToastVariant,
      message: string,
      isVisible: boolean = true
    ) {
      const newNotification: INotification = {
        id: generateUniqueId(),
        ToastVariant: toastVariant,
        message,
        isVisible,
      };
      this.notifications.push(newNotification);
    },
    removeNotification(notification: INotification) {
      const index = this.notifications.findIndex((n) => n.id === notification.id);
      if (index !== -1) {
        this.notifications.splice(index, 1);
      }
    },
    updateNotificationVisibility(notification: INotification, isVisible: boolean) {
      const index = this.notifications.findIndex((n) => n.id === notification.id);
      if (index !== -1) {
        this.notifications[index].isVisible = isVisible;
      }
    },
    clearExpiredNotifications() {
      this.notifications.forEach((notification) => {
        if (!notification.isVisible) {
          this.removeNotification(notification);
        }
      });
    },
    addErrorNotification(message: string) {
      this.addNotification(ToastVariant.ERROR, message, true);
    },
    addSuccessNotification(message: string) {
      this.addNotification(ToastVariant.SUCCESS, message, true);
    },
    addWarningNotification(message: string) {
      this.addNotification(ToastVariant.WARNING, message, true);
    },
    // Returns a delay in milliseconds for auto-dismiss based on the notification's index.
    getAutoDismissDelay(index: number): number {
      return this.notifications.length + (index + 1) * 5000;
    },
  }
});
